import { createFileRoute, Link } from "@tanstack/react-router";
import { SetupCopyBlock } from "@/components/setup/SetupCopyBlock";

export const Route = createFileRoute("/docs/creating-posts")({
	component: CreatingPosts,
});

function CreatingPosts() {
	return (
		<article className="space-y-10">
			<div className="space-y-2">
				<h1 className="text-2xl font-medium tracking-tight">Creating posts</h1>
				<p className="text-sm text-muted-foreground">
					How to create and publish content.
				</p>
			</div>
			<div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
				<p>Tell your assistant things like:</p>
				<div className="grid gap-2">
					<SetupCopyBlock
						text="Help me draft a post about our spring sale, then ask me to approve the final caption."
						multiline
					/>
					<SetupCopyBlock
						text="Create a post with this exact caption: ..."
						multiline
					/>
					<SetupCopyBlock
						text="Use this approved caption and schedule it for Friday at 10am."
						multiline
					/>
					<SetupCopyBlock
						text="Create a draft from this generated image with this exact caption, using the actual image bytes."
						multiline
					/>
				</div>
				<p>
					The assistant uses the{" "}
					<code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono text-foreground">
						postCompose
					</code>{" "}
					tool after you choose or approve the exact caption. It can help you
					draft first in chat, but it should not create, schedule, or publish a
					post until the caption is confirmed. For a generated image or video
					that should become a post immediately, the preferred tool is{" "}
					<code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono text-foreground">
						postCompose
					</code>
					. It uploads the media and creates the draft or scheduled post in one
					step.
				</p>
				<p>
					ChatGPT sandbox paths such as{" "}
					<code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono text-foreground">
						/mnt/data/...
					</code>{" "}
					are not public URLs. The assistant must send the actual media bytes as
					base64 for small files, or use{" "}
					<code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono text-foreground">
						mediaPrepare
					</code>{" "}
					for larger files. Local MCP runtimes can pass a{" "}
					<code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono text-foreground">
						filePath
					</code>{" "}
					to stream a binary multipart upload from disk, but hosted ChatGPT
					connectors still need bytes or a direct upload URL. Use{" "}
					<code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono text-foreground">
						mediaPrepare
					</code>{" "}
					when you only want to add media to the library for later reuse.
				</p>
				<p>
					Once created, your posts appear in the{" "}
					<strong className="text-foreground">Posts</strong> tab and the{" "}
					<strong className="text-foreground">Calendar</strong> on the
					dashboard.
				</p>
			</div>

			<div className="flex items-center gap-4 text-sm">
				<Link
					to="/docs/quick-start"
					className="text-muted-foreground hover:text-foreground transition-colors"
				>
					&larr; Quick start
				</Link>
				<Link
					to="/docs/editing-posts"
					className="text-primary hover:text-primary/80 transition-colors ml-auto"
				>
					Editing posts &rarr;
				</Link>
			</div>
		</article>
	);
}
